import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { MATERIAL_IMPORTS } from '../../shared/material';
import { AdminApiService } from '../../core/services/admin-api.service';
import { AuthService } from '../../core/services/auth.service';
import { AdminBlocklistedNameDto } from '../../core/models/admin-api.model';

@Component({
    selector: 'app-blocklist-names',
    standalone: true,
    imports: [CommonModule, FormsModule, ...MATERIAL_IMPORTS],
    templateUrl: './blocklist-names.component.html',
    styleUrl: './blocklist-names.component.scss'
})
export class BlocklistNamesComponent implements OnInit {
    private readonly adminApi = inject(AdminApiService);
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly route = inject(ActivatedRoute);

    items: AdminBlocklistedNameDto[] = [];

    itemToDelete: AdminBlocklistedNameDto | null = null;
    isDeleting = false;

    isLoading = true;
    isSaving = false;
    error: string | null = null;

    editingId: number | null = null;
    name = '';
    description = '';
    searchTerm = '';

    sortColumn: 'name' | 'createdAt' = 'name';
    sortDirection: 'asc' | 'desc' = 'asc';

    ngOnInit(): void {
        this.loadItems();
    }

    goBack(): void {
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

        if (returnUrl) {
            this.router.navigateByUrl(returnUrl);
            return;
        }

        this.router.navigate(['/showsList']);
    }

    logout(): void {
        this.authService.logout();
    }

    loadItems(): void {
        this.isLoading = true;
        this.error = null;

        this.adminApi.getBlocklistNames().subscribe({
            next: items => {
                this.items = items;
                this.isLoading = false;
            },
            error: error => {
                this.error = error?.error?.detail || 'Blocklist konnte nicht geladen werden.';
                this.isLoading = false;
            }
        });
    }

    save(): void {
        const normalizedName = this.name.trim().replace(/\s+/g, ' ');
        const normalizedDescription = this.description.trim().replace(/\s+/g, ' ');

        if (!normalizedName) {
            this.error = 'Bitte einen Namen eingeben.';
            return;
        }

        this.isSaving = true;
        this.error = null;

        this.adminApi.upsertBlocklistName({
            id: this.editingId,
            name: normalizedName,
            description: normalizedDescription || null
        }).subscribe({
            next: () => {
                this.resetForm();
                this.isSaving = false;
                this.loadItems();
            },
            error: error => {
                this.error = error?.error?.detail || 'Der Eintrag konnte nicht gespeichert werden.';
                this.isSaving = false;
            }
        });
    }

    edit(item: AdminBlocklistedNameDto): void {
        this.editingId = item.id;
        this.name = item.name;
        this.description = item.description ?? '';
    }

    openDeleteConfirm(item: AdminBlocklistedNameDto): void {
        this.itemToDelete = item;
    }

    closeDeleteConfirm(): void {
        if (this.isDeleting) {
            return;
        }

        this.itemToDelete = null;
    }

    confirmDelete(): void {
        if (!this.itemToDelete) {
            return;
        }

        this.isDeleting = true;
        this.error = null;

        this.adminApi.deleteBlocklistName(this.itemToDelete.id).subscribe({
            next: () => {
                this.isDeleting = false;
                this.itemToDelete = null;
                this.loadItems();
            },
            error: error => {
                this.error = error?.error?.detail || 'Der Eintrag konnte nicht gelöscht werden.';
                this.isDeleting = false;
                this.itemToDelete = null;
            }
        });
    }

    resetForm(): void {
        this.editingId = null;
        this.name = '';
        this.description = '';
        this.error = null;
    }

    get filteredItems(): AdminBlocklistedNameDto[] {
        const search = this.searchTerm.trim().toLowerCase();

        let result = !search
            ? [...this.items]
            : this.items.filter(item =>
                item.name.toLowerCase().includes(search)
            );

        result = result.sort((a, b) => {
            let compareValue = 0;

            if (this.sortColumn === 'name') {
                compareValue = a.name.localeCompare(b.name, 'de');
            }

            if (this.sortColumn === 'createdAt') {
                compareValue = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            }

            return this.sortDirection === 'asc' ? compareValue : -compareValue;
        });

        return result;
    }

    formatDate(value: string): string {
        return formatDate(value, 'dd.MM.yyyy HH:mm', 'de-DE');
    }

    trackById(_: number, item: AdminBlocklistedNameDto): number {
        return item.id;
    }

    toggleSort(column: 'name' | 'createdAt'): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            return;
        }

        this.sortColumn = column;
        this.sortDirection = 'asc';
    }

    getSortIcon(column: 'name' | 'createdAt'): string {
        if (this.sortColumn !== column) {
            return 'unfold_more';
        }

        return this.sortDirection === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
    }

}